import { createBrowserClient } from '@supabase/ssr'
import { toast } from 'sonner'
import { Database } from '@/types/database.types'
import {
  checkResponseForRLSError,
  getRLSErrorMessage,
  reportRLSError,
} from './rls-error-handler'

// RLS 에러 토스트 중복 방지용 (같은 에러 1초 내 재표시 방지)
let lastRLSErrorTime = 0
let lastRLSErrorKey = ''

/**
 * RLS 에러 전용 토스트를 표시합니다.
 * 검은색 스타일로 일관되게 표시됩니다.
 */
function showRLSErrorToast(message: string, table?: string): void {
  // 서버 환경에서는 toast 표시하지 않음 (sonner는 클라이언트 전용)
  if (typeof window === 'undefined') {
    return
  }

  const now = Date.now()
  const errorKey = `${message}-${table}`

  // 같은 에러 1초 내 중복 방지
  if (errorKey === lastRLSErrorKey && now - lastRLSErrorTime < 1000) {
    return
  }

  lastRLSErrorTime = now
  lastRLSErrorKey = errorKey

  toast.error(message, {
    style: {
      background: '#1a1a1a',
      color: '#ffffff',
      border: '1px solid #333',
    },
    duration: 5000,
  })
}

/**
 * RLS 에러를 감지하는 커스텀 fetch wrapper
 * - 토스트로 사용자에게 알림
 * - 상세 진단 정보를 콘솔 로그로 출력 (디버깅용)
 */
function createRLSAwareFetch(): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await fetch(input, init)
    const url = typeof input === 'string' ? input : input.toString()

    // RLS 에러 체크 (응답 클론하여 체크)
    const rlsInfo = await checkResponseForRLSError(response)

    if (rlsInfo.isRLSError) {
      const message = getRLSErrorMessage(rlsInfo)
      showRLSErrorToast(message, rlsInfo.table)

      // 콘솔 로깅
      reportRLSError(rlsInfo, {
        url,
        method: init?.method || 'GET',
        extra: {
          environment: 'browser',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        },
      })
    }

    return response
  }
}

export function createClient() {
  return createBrowserClient<Database>(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
    {
      global: {
        fetch: createRLSAwareFetch(),
      },
    }
  )
}
